import { Form } from "antd";
import { useForm } from "antd/es/form/Form";
import type { FC } from "react";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import type { UserLogEntity } from "src/app/features/UserLog/types/UserLogEntity";
import { SelectDynamic, TextInput } from "../../../../../components/Form/formComponents";
import { DatePickerInput } from "../../../../../components/Form/formComponents/DatePicker/DatePicker";
import { NumberInput } from "../../../../../components/Form/formComponents/Number/NumberInput";
import type { SearchFormProps } from "../../../../../components/Form/types/types";
import { revisionTypeSelectorConfig } from "../../../../../components/SearchForm/FormSearchUtils";
import { SearchForm } from "../../../../../components/SearchForm/SearchForm";
import { getEmailFromToken, getUserNameFromToken } from "../../../../../core/router/auth/SecurityManager";

export const UserLogSearchForm: FC<SearchFormProps<UserLogEntity>> = ({ searchCallback }) => {
    const { t } = useTranslation();
    // eslint-disable-next-line no-underscore-dangle
    const isMSEntraLogin = window.__RUNTIME_CONFIG__?.IDENTITY_SERVICE === "entra";
    const user = isMSEntraLogin ? getEmailFromToken() : getUserNameFromToken();
    const [form] = useForm();
    const initialSearchTriggered = useRef(false);

    // Automatically trigger search on mount with the initial username value
    useEffect(() => {
        if (searchCallback && user && !initialSearchTriggered.current) {
            form.setFieldsValue({ username: user });
            searchCallback({ username: user });
            initialSearchTriggered.current = true;
        }
    }, [searchCallback, user, form]);

    // Custom reset handler to clear the username field and reset table
    const handleReset = () => {
        form.resetFields();
        form.setFieldsValue({ username: undefined });
        if (searchCallback) {
            searchCallback({});
        }
    };

    return (
        <SearchForm searchCallback={searchCallback} form={form} onReset={handleReset}>
            <Form.Item label={t("form.system.id")} name="entityId">
                <NumberInput name="entityId" readOnly={false} />
            </Form.Item>
            <Form.Item label={t("user.log.form.revisionType")} name="revisionType">
                <SelectDynamic name="revisionType" label={t("form.operation")} fieldConfig={revisionTypeSelectorConfig} />
            </Form.Item>
            <Form.Item label={t("form.createdFrom")} name="createdFrom">
                <DatePickerInput name="createdFrom" readOnly={false} />
            </Form.Item>
            <Form.Item label={t("form.createdTo")} name="createdTo">
                <DatePickerInput name="createdTo" readOnly={false} />
            </Form.Item>
            <Form.Item label={t("form.user.username")} name="username">
                <TextInput name="username" />
            </Form.Item>
        </SearchForm>
    );
};
